import { cn } from '../ui/utils';
import { subscoreEntries, type OrganScore } from './organHealth';

/**
 * The per-parameter sub-scores as a small box grid — one box per functional
 * axis (e.g. albumin, INR, bilirubin for liver). Values are the 0–100
 * transfer-curve outputs the scorer persists in `structured_fields.subscores`,
 * NOT raw labs (those aren't sent, so we never fabricate them). For worst-of
 * organs (liver, lung) the leading axis — the one that set the base — is marked
 * "leads", making the min-combine visible rather than opaque.
 *
 * Renders nothing when the backend sent no sub-scores (kidney-without-UACR,
 * insufficient data, older rows), so it's safe to always mount.
 */
export function SubscoreGrid({ organ }: { organ: OrganScore }) {
  const entries = subscoreEntries(organ);
  if (entries.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {entries.map((e) => (
        <div
          key={e.key}
          className={cn(
            'rounded border bg-surface-sunken/40 px-3 py-2',
            e.leads ? 'border-band-severe/40' : 'border-border-subtle',
          )}
        >
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-text-tertiary">{e.label}</span>
            {e.leads ? (
              <span className="rounded-sm bg-band-severe/10 px-1 text-[9px] font-medium uppercase tracking-wide text-band-severe-text">
                leads
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 text-lg font-medium tabular-nums text-text-primary">{e.value}</div>
        </div>
      ))}
    </div>
  );
}
